import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import mondaySdk from 'monday-sdk-js';
import type { MondayClientSdk } from 'monday-sdk-js/types/client-sdk.interface';
import { setSyncfusionDarkTheme } from '../utils/syncfusion-theme';

const monday: MondayClientSdk = mondaySdk();

/** Shape of the context data provided by monday.com */
export interface MondayContextData {
  /** The connected board ID (available in Board View) */
  boardId: number | null;
  /** The connected board IDs (available in Dashboard Widget) */
  boardIds: number[];
  /** The focused item ID (available in Item View) */
  itemId: number | null;
  /** Current user ID */
  userId: number | null;
  /** Current account info */
  account: { id: number; name: string } | null;
  /** Current monday.com theme: "light" or "dark" */
  theme: 'light' | 'dark';
  /** Workspace ID */
  workspaceId: number | null;
}

export interface MondayContextValue {
  /** The monday SDK instance */
  mondayInstance: MondayClientSdk;
  /** Context data from monday.com */
  context: MondayContextData;
  /** Whether the context has finished loading */
  isReady: boolean;
  /** Whether the current user is a plugin admin */
  isAdmin: boolean;
  /** Whether the current user is a viewer (no API access) */
  isViewer: boolean;
  /** Get a session token for API authentication */
  getSessionToken: () => Promise<string>;
}

const defaultContext: MondayContextData = {
  boardId: null,
  boardIds: [],
  itemId: null,
  userId: null,
  account: null,
  theme: 'light',
  workspaceId: null,
};

const MondayContext = createContext<MondayContextValue | null>(null);

interface MondayProviderProps {
  children: ReactNode;
}

export const MondayProvider: React.FC<MondayProviderProps> = ({ children }) => {
  const [context, setContext] = useState<MondayContextData>(defaultContext);
  const [isReady, setIsReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isViewer, setIsViewer] = useState(false);

  useEffect(() => {
    // Initialize the monday SDK - tells the host iframe we are ready
    monday.execute('valueCreatedForUser');

    // Listen for context changes from the monday.com host
    monday.listen('context', (res) => {
      const data = res.data as Record<string, unknown>;
      const user = data.user as Record<string, unknown> | undefined;

      setContext((prev) => ({
        ...prev,
        boardId: (data.boardId as number) ?? prev.boardId,
        boardIds: (data.boardIds as number[]) ?? prev.boardIds,
        itemId: (data.itemId as number) ?? prev.itemId,
        userId: (user?.id as number) ?? (data.userId as number) ?? prev.userId,
        account: (data.account as { id: number; name: string }) ?? prev.account,
        theme: (data.theme as 'light' | 'dark') ?? prev.theme,
        workspaceId: (data.workspaceId as number) ?? prev.workspaceId,
      }));

      setIsReady(true);
    });

    // Apply theme class to body for CSS variable support + swap Syncfusion theme
    monday.listen('context', (res) => {
      const theme = (res.data as Record<string, unknown>).theme as string | undefined;
      const isDark = theme === 'dark';
      if (isDark) {
        document.body.classList.add('dark-app-theme');
      } else {
        document.body.classList.remove('dark-app-theme');
      }
      setSyncfusionDarkTheme(isDark);
    });
  }, []);

  // Fetch current user's admin status and detect viewer role
  useEffect(() => {
    if (!isReady) return;
    monday.api('{ me { is_admin is_view_only } }').then((res) => {
      const me = (res as { data?: { me?: { is_admin?: boolean; is_view_only?: boolean } } }).data?.me;
      setIsAdmin(me?.is_admin === true);
      setIsViewer(me?.is_view_only === true);
    }).catch(() => {
      // If the API call fails entirely, the user is likely a viewer
      setIsAdmin(false);
      setIsViewer(true);
    });
  }, [isReady]);

  const getSessionToken = useCallback(async (): Promise<string> => {
    const result = await monday.get('sessionToken');
    return result.data as string;
  }, []);

  const value: MondayContextValue = {
    mondayInstance: monday,
    context,
    isReady,
    isAdmin,
    isViewer,
    getSessionToken,
  };

  return (
    <MondayContext.Provider value={value}>
      {children}
    </MondayContext.Provider>
  );
};

/**
 * Access the monday.com context. Must be used within a MondayProvider.
 */
export const useMondayContext = (): MondayContextValue => {
  const ctx = useContext(MondayContext);
  if (!ctx) {
    throw new Error('useMondayContext must be used within a MondayProvider');
  }
  return ctx;
};

export default MondayContext;
